import { Box, Button, Flex, Heading, Text, VStack, HStack } from "@chakra-ui/react"
import { Routes, Route, Navigate, NavLink, useNavigate } from "react-router-dom"
import { useFirebaseAuth } from "../firebase/auth-context"
import ResortDashboard from "../features/resorts/ResortDashboard"
import ResortDetail from "../features/resorts/ResortDetail"
import FeatureDetail from "../features/resorts/FeatureDetail"
import PlaceList from "../features/places/PlaceList"
import PlaceDetail from "../features/places/PlaceDetail"
import UserList from "../features/users/UserList"
import UserDetail from "../features/users/UserDetail"
import PipelineVersionDashboard from "../features/pipeline/PipelineVersionDashboard"
import HealthDashboard from "../features/health/HealthDashboard"
import PromoCodes from "../features/promo-codes/PromoCodes"
import ResortVisitSummary from "../features/resorts/ResortVisitSummary"
import ReviewFeedbackDashboard from "../features/reviews/ReviewFeedbackDashboard"
import PreparedPushesDashboard from "../features/prepared-pushes/PreparedPushesDashboard"
import OpeningsHistoryDashboard from "../features/openings/OpeningsHistoryDashboard"

export default function AppShell() {
  const { user, status, token, signInWithGoogle, signOut, error } = useFirebaseAuth()
  const navigate = useNavigate()
  const isSignedIn = status === "signed-in" && Boolean(token) && Boolean(user)
  const isAuthLoading = status === "loading"
  const authAlert = error?.message ?? null

  if (!isSignedIn) {
    return (
      <Flex h="100vh" bg="gray.900" color="gray.100" align="center" justify="center">
        <VStack gap="4" bg="gray.800" p="12" borderRadius="lg">
          <Button colorPalette="blue" loading={isAuthLoading} onClick={signInWithGoogle}>
            Sign in with Google
          </Button>
        </VStack>
      </Flex>
    )
  }

  return (
    <Flex minH="100vh" bg="gray.900" color="gray.100">
      <Box w="260px" bg="gray.800" p="4">
        <Heading size="md" mb="6">
          Admin
        </Heading>
      </Box>
      <Box flex="1" p="6" bg="gray.900">
        <Flex justify="space-between" align="center" mb="4">
          <HStack gap="4">
            <NavLink to="/resorts">
              {({ isActive }) => (
                <Button
                  variant={isActive ? "solid" : "ghost"}
                  colorPalette="blue"
                  color={isActive ? "white" : "gray.300"}
                >
                  Resorts
                </Button>
              )}
            </NavLink>
            <NavLink to="/resort-visits">
              {({ isActive }) => (
                <Button
                  variant={isActive ? "solid" : "ghost"}
                  colorPalette="blue"
                  color={isActive ? "white" : "gray.300"}
                >
                  Resort Visits
                </Button>
              )}
            </NavLink>
            <NavLink to="/lift-openings">
              {({ isActive }) => (
                <Button variant={isActive ? "solid" : "ghost"} colorPalette="blue" color={isActive ? "white" : "gray.300"}>Lift openings</Button>
              )}
            </NavLink>
            <NavLink to="/places">
              {({ isActive }) => (
                <Button
                  variant={isActive ? "solid" : "ghost"}
                  colorPalette="blue"
                  color={isActive ? "white" : "gray.300"}
                >
                  Places
                </Button>
              )}
            </NavLink>
            <NavLink to="/users">
              {({ isActive }) => (
                <Button
                  variant={isActive ? "solid" : "ghost"}
                  colorPalette="blue"
                  color={isActive ? "white" : "gray.300"}
                >
                  Users
                </Button>
              )}
            </NavLink>
            <NavLink to="/pipeline-versions">
              {({ isActive }) => (
                <Button
                  variant={isActive ? "solid" : "ghost"}
                  colorPalette="blue"
                  color={isActive ? "white" : "gray.300"}
                >
                  Pipeline Versions
                </Button>
              )}
            </NavLink>
            <NavLink to="/health">
              {({ isActive }) => (
                <Button
                  variant={isActive ? "solid" : "ghost"}
                  colorPalette="blue"
                  color={isActive ? "white" : "gray.300"}
                >
                  Healthchecks
                </Button>
              )}
            </NavLink>
            <NavLink to="/promo-codes">
              {({ isActive }) => (
                <Button
                  variant={isActive ? "solid" : "ghost"}
                  colorPalette="blue"
                  color={isActive ? "white" : "gray.300"}
                >
                  Promo Codes
                </Button>
              )}
            </NavLink>
            <NavLink to="/reviews">
              {({ isActive }) => (
                <Button
                  variant={isActive ? "solid" : "ghost"}
                  colorPalette="blue"
                  color={isActive ? "white" : "gray.300"}
                >
                  Reviews
                </Button>
              )}
            </NavLink>
            <NavLink to="/prepared-pushes">
              {({ isActive }) => (
                <Button
                  variant={isActive ? "solid" : "ghost"}
                  colorPalette="blue"
                  color={isActive ? "white" : "gray.300"}
                >
                  Prepared Pushes
                </Button>
              )}
            </NavLink>
          </HStack>
          <Button
            variant="surface"
            colorPalette="red"
            size="sm"
            loading={isAuthLoading}
            onClick={() => {
              signOut()
              navigate("/")
            }}
          >
            Sign out
          </Button>
        </Flex>
        {authAlert && (
          <Box mb="4" bg="red.700" px="3" py="2" borderRadius="md">
            <Text fontSize="sm" color="white">
              {authAlert}
            </Text>
          </Box>
        )}
        <Routes>
          <Route path="/" element={<Navigate to="/resorts" replace />} />
          <Route path="/resorts" element={<ResortDashboard />} />
          <Route path="/resort-visits" element={<ResortVisitSummary />} />
          <Route path="/lift-openings" element={<OpeningsHistoryDashboard />} />
          <Route path="/resorts/:uid/features/:featureUid/*" element={<FeatureDetail />} />
          <Route path="/resorts/:uid/map" element={<ResortDetail />} />
          <Route path="/resorts/:uid/features" element={<ResortDetail />} />
          <Route path="/resorts/:uid/snow-reports" element={<ResortDetail />} />
          <Route path="/resorts/:uid/openings" element={<ResortDetail />} />
          <Route path="/resorts/:uid" element={<ResortDetail />} />
          <Route path="/places" element={<PlaceList />} />
          <Route path="/places/:id" element={<PlaceDetail />} />
          <Route path="/users" element={<UserList />} />
          <Route path="/users/:uid" element={<UserDetail />} />
          <Route path="/pipeline-versions" element={<PipelineVersionDashboard />} />
          <Route path="/health" element={<HealthDashboard token={token!} />} />
          <Route path="/promo-codes" element={<PromoCodes />} />
          <Route path="/reviews" element={<ReviewFeedbackDashboard />} />
          <Route path="/prepared-pushes" element={<PreparedPushesDashboard />} />
        </Routes>
      </Box>
    </Flex>
  )
}
